import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';

const SCRIPT_ID = 'cf-turnstile-script';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

/**
 * Cloudflare Turnstile widget. Calls onToken with the challenge token (or '').
 * Pass a changing resetKey to force a fresh challenge after successful submit.
 */
const TurnstileField = ({ onToken, onExpire, resetKey = 0 }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
  }, [onToken, onExpire]);

  const destroyWidget = useCallback(() => {
    if (widgetIdRef.current != null && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        // Widget may already be gone
      }
      widgetIdRef.current = null;
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
  }, []);

  const renderWidget = useCallback(() => {
    if (!window.turnstile || !containerRef.current || !SITE_KEY) return;

    destroyWidget();

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      callback: (token) => onTokenRef.current?.(token),
      'expired-callback': () => {
        onTokenRef.current?.('');
        onExpireRef.current?.();
      },
      'error-callback': () => onTokenRef.current?.(''),
    });
  }, [destroyWidget]);

  useEffect(() => {
    if (!SITE_KEY) return undefined;

    const onScriptReady = () => renderWidget();

    if (window.turnstile) {
      onScriptReady();
    } else {
      let script = document.getElementById(SCRIPT_ID);
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
      script.addEventListener('load', onScriptReady);
    }

    return () => {
      destroyWidget();
      const script = document.getElementById(SCRIPT_ID);
      if (script) script.removeEventListener('load', onScriptReady);
    };
  }, [renderWidget, destroyWidget, resetKey]);

  if (!SITE_KEY) {
    return (
      <Typography sx={{ mb: 2, color: '#b91c1c', fontSize: '0.85rem' }}>
        CAPTCHA is not configured. Set REACT_APP_TURNSTILE_SITE_KEY.
      </Typography>
    );
  }

  return <Box ref={containerRef} sx={{ mb: 2, minHeight: 65 }} />;
};

export default TurnstileField;
